import { useRef, useState } from 'react';
import { Check, ChevronLeft, Lock } from 'lucide-react';
import { CardBrandIcon, Input, Label } from '@/components/ui';
import { cardFieldClass, payButtonClass } from './paymentFormClasses';
import {
  detectCardBrand,
  expectedCvvLength,
  formatCardNumber,
  formatExpiry,
  maxCardDigits,
  parseExpiry,
  passesLuhn,
  type CardBrand,
} from '@/lib/card-formatting';

export interface NewCardFormData {
  cardNumber: string;
  cardholderName: string;
  expiryMonth: string;
  expiryYear: string;
  cvv: string;
}

interface FieldErrors {
  cardNumber?: string;
  cardholderName?: string;
  expiry?: string;
  cvv?: string;
}

interface Props {
  isSubmitting: boolean;
  savePaymentMethod: boolean;
  onSavePaymentMethodChange: (value: boolean) => void;
  onBack?: () => void;
  /** Visible label on the back link — names the screen Back returns to. */
  backLabel?: string;
  onSubmit: (data: NewCardFormData) => void;
  /** Visible label on the submit button — usually "Pay $X.XX". */
  submitLabel: string;
}

interface InternalForm {
  cardNumberRaw: string;
  cardholderName: string;
  expiry: string;
  cvv: string;
}

export function NewCardForm({
  isSubmitting,
  savePaymentMethod,
  onSavePaymentMethodChange,
  onBack,
  backLabel = 'Deposit',
  onSubmit,
  submitLabel,
}: Props) {
  const [form, setForm] = useState<InternalForm>({
    cardNumberRaw: '',
    cardholderName: '',
    expiry: '',
    cvv: '',
  });
  const [touched, setTouched] = useState<Record<keyof InternalForm, boolean>>({
    cardNumberRaw: false,
    cardholderName: false,
    expiry: false,
    cvv: false,
  });
  const expiryRef = useRef<HTMLInputElement>(null);
  const cvvRef = useRef<HTMLInputElement>(null);

  const brand: CardBrand = detectCardBrand(form.cardNumberRaw);
  const formattedCardNumber = formatCardNumber(form.cardNumberRaw, brand);
  const expiryParsed = parseExpiry(form.expiry);

  const cardDigits = form.cardNumberRaw.replace(/\D/g, '');
  const errors: FieldErrors = {};
  if (touched.cardNumberRaw) {
    if (cardDigits.length === 0) errors.cardNumber = 'Enter your card number.';
    else if (cardDigits.length < 13) errors.cardNumber = 'That card number looks too short.';
    else if (!passesLuhn(cardDigits)) errors.cardNumber = 'Double-check the card number.';
  }
  if (touched.cardholderName && !form.cardholderName.trim()) {
    errors.cardholderName = 'Enter the name on the card.';
  }
  if (touched.expiry) {
    if (!form.expiry) errors.expiry = 'Required.';
    else if (form.expiry.replace(/\D/g, '').length < 4) errors.expiry = 'MM/YY';
    else if (!expiryParsed.isValid) errors.expiry = 'That date is invalid or in the past.';
  }
  if (touched.cvv) {
    const need = expectedCvvLength(brand);
    if (form.cvv.length < need) errors.cvv = `${need} digits.`;
  }

  const allFieldsFilled =
    cardDigits.length >= 13 &&
    form.cardholderName.trim().length > 0 &&
    expiryParsed.isValid &&
    form.cvv.length === expectedCvvLength(brand);
  const hasErrors = Object.keys(errors).length > 0;
  const canSubmit = allFieldsFilled && !hasErrors && !isSubmitting;

  function setField<K extends keyof InternalForm>(key: K, value: InternalForm[K]) {
    setForm((prev) => ({ ...prev, [key]: value }));
  }

  function markTouched(key: keyof InternalForm) {
    setTouched((prev) => (prev[key] ? prev : { ...prev, [key]: true }));
  }

  function handleCardNumberChange(value: string) {
    const digits = value.replace(/\D/g, '').slice(0, maxCardDigits(detectCardBrand(value)));
    setField('cardNumberRaw', digits);
    if (digits.length === maxCardDigits(detectCardBrand(digits))) {
      // auto-advance once the card number is full
      requestAnimationFrame(() => expiryRef.current?.focus());
    }
  }

  function handleExpiryChange(value: string) {
    const formatted = formatExpiry(value);
    setField('expiry', formatted);
    if (formatted.replace(/\D/g, '').length === 4) {
      requestAnimationFrame(() => cvvRef.current?.focus());
    }
  }

  function handleSubmit(e: React.SyntheticEvent<HTMLFormElement>) {
    e.preventDefault();
    setTouched({ cardNumberRaw: true, cardholderName: true, expiry: true, cvv: true });
    if (!canSubmit) return;
    onSubmit({
      cardNumber: form.cardNumberRaw,
      cardholderName: form.cardholderName.trim(),
      expiryMonth: expiryParsed.month,
      expiryYear: expiryParsed.year,
      cvv: form.cvv,
    });
  }

  const onBlur = (key: keyof InternalForm) => () => markTouched(key);

  function clearForm() {
    setForm({ cardNumberRaw: '', cardholderName: '', expiry: '', cvv: '' });
    setTouched({ cardNumberRaw: false, cardholderName: false, expiry: false, cvv: false });
  }

  return (
    <form onSubmit={handleSubmit} className="flex flex-col gap-4" noValidate>
      <div className="flex items-center justify-between">
        {onBack ? (
          <button
            type="button"
            onClick={onBack}
            className="-ml-1 flex items-center gap-1 rounded px-1 text-sm font-medium text-purple hover:opacity-80 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-purple/40"
          >
            <ChevronLeft className="size-4" strokeWidth={2.5} /> {backLabel}
          </button>
        ) : (
          <span aria-hidden="true" />
        )}
        <span className="inline-flex items-center gap-1 text-[10px] font-medium uppercase tracking-wide text-ink-muted">
          <Lock className="size-3" /> Encrypted
        </span>
      </div>

      <h2 className="text-[18px] font-semibold text-ink">Card Details</h2>

      <div className="flex flex-col gap-1.5">
        <Label htmlFor="card-number" className="px-3 text-[16px] text-ink">
          Card Number
        </Label>
        <div className="relative">
          <Input
            id="card-number"
            inputMode="numeric"
            autoComplete="cc-number"
            placeholder="1111-1111-1111-1111"
            value={formattedCardNumber}
            onChange={(e) => handleCardNumberChange(e.target.value)}
            onBlur={onBlur('cardNumberRaw')}
            aria-invalid={!!errors.cardNumber}
            aria-describedby={errors.cardNumber ? 'card-number-error' : undefined}
            className={`${cardFieldClass} pr-12 font-mono tracking-wider`}
          />
          <span className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none">
            <CardBrandIcon brand={brand} size={32} />
          </span>
        </div>
        {errors.cardNumber && (
          <p id="card-number-error" className="text-xs text-danger-alt">
            {errors.cardNumber}
          </p>
        )}
      </div>

      <div className="flex flex-col gap-1.5">
        <Label htmlFor="card-name" className="px-3 text-[16px] text-ink">
          Name on Card
        </Label>
        <Input
          id="card-name"
          autoComplete="cc-name"
          placeholder="Ex: John Doe"
          value={form.cardholderName}
          onChange={(e) => setField('cardholderName', e.target.value)}
          onBlur={onBlur('cardholderName')}
          aria-invalid={!!errors.cardholderName}
          className={cardFieldClass}
        />
        {errors.cardholderName && <p className="text-xs text-danger-alt">{errors.cardholderName}</p>}
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="card-expiry" className="px-3 text-[16px] text-ink">
            Expiry
          </Label>
          <Input
            id="card-expiry"
            ref={expiryRef}
            inputMode="numeric"
            autoComplete="cc-exp"
            maxLength={5}
            placeholder="MM/YY"
            value={form.expiry}
            onChange={(e) => handleExpiryChange(e.target.value)}
            onBlur={onBlur('expiry')}
            aria-invalid={!!errors.expiry}
            className={`${cardFieldClass} font-mono`}
          />
          {errors.expiry && <p className="text-xs text-danger-alt">{errors.expiry}</p>}
        </div>
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="card-cvv" className="px-3 text-[16px] text-ink">
            CVV
          </Label>
          <Input
            id="card-cvv"
            ref={cvvRef}
            inputMode="numeric"
            autoComplete="cc-csc"
            maxLength={expectedCvvLength(brand)}
            placeholder="XXX"
            value={form.cvv}
            onChange={(e) => setField('cvv', e.target.value.replace(/\D/g, ''))}
            onBlur={onBlur('cvv')}
            aria-invalid={!!errors.cvv}
            className={`${cardFieldClass} font-mono`}
          />
          {errors.cvv && <p className="text-xs text-danger-alt">{errors.cvv}</p>}
        </div>
      </div>

      <div className="flex items-center justify-between">
        <label className="flex items-center gap-2.5 text-sm cursor-pointer">
          {/* Design 1940-140242: white box, green check — not the browser's
              green-filled accent checkbox. */}
          <span className="relative flex size-5 shrink-0 items-center justify-center">
            <input
              type="checkbox"
              checked={savePaymentMethod}
              onChange={(e) => onSavePaymentMethodChange(e.target.checked)}
              className="peer size-5 appearance-none rounded-md border-2 border-stroke-soft bg-surface transition-colors checked:border-success focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-purple/40"
            />
            <Check
              aria-hidden="true"
              className="pointer-events-none absolute size-3.5 text-success opacity-0 transition-opacity peer-checked:opacity-100"
              strokeWidth={3.5}
            />
          </span>
          <span className="font-medium text-ink">Remember my card details.</span>
        </label>
        <button
          type="button"
          onClick={clearForm}
          className="text-sm font-medium text-purple hover:opacity-80 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-purple/40 rounded px-1"
        >
          Remove Card
        </button>
      </div>

      <div className="flex items-center justify-center gap-3 rounded-pill bg-accent-tint/60 py-2.5">
        <CardBrandIcon brand="visa" size={36} />
        <CardBrandIcon brand="amex" size={36} />
        <CardBrandIcon brand="mastercard" size={36} />
        <CardBrandIcon brand="discover" size={36} />
      </div>

      <p className="text-center text-xs text-ink-muted">
        *This charge will appear on your card statement as CROWNPREDICTIONS
      </p>

      <button type="submit" disabled={!canSubmit} className={payButtonClass}>
        {isSubmitting ? 'Processing…' : submitLabel}
      </button>
    </form>
  );
}
